import React, { useState, useRef, useEffect, useContext } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useLocation } from "react-router-dom";
import { Bell, Check, CheckCheck, Heart, Trophy, Wallet, ShoppingBag, AlertCircle, Gift, X, ShieldAlert } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { notificationsService } from "../services/notificationsService";
import OrgThemeContext from "../context/OrgThemeContext";
import AuthContext from "../context/AuthContext";
import { formatTabTitle } from "../utils/tabTitle";
import { useTranslation, useFormat } from "../i18n";

const typeIcons = {
  recognition_created: <Heart size={16} className="text-pink-500" />,
  recognition_approved: <Check size={16} className="text-green-500" />,
  recognition_rejected: <X size={16} className="text-red-500" />,
  nomination_submitted: <Trophy size={16} className="text-amber-500" />,
  nomination_approved: <Trophy size={16} className="text-green-500" />,
  campaign_started: <Gift size={16} className="text-blue-500" />,
  campaign_ended: <Gift size={16} className="text-gray-500" />,
  budget_exhausted: <AlertCircle size={16} className="text-red-500" />,
  wallet_credited: <Wallet size={16} className="text-green-500" />,
  reward_redeemed: <ShoppingBag size={16} className="text-purple-500" />,
  approval_pending: <ShieldAlert size={16} className="text-orange-500" />,
  system_alert: <AlertCircle size={16} className="text-blue-500" />,
};

const NotificationBell = () => {
  const { t } = useTranslation("nav");
  const fmt = useFormat();
  const [isOpen, setIsOpen] = useState(false);
  const panelRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const orgTheme = useContext(OrgThemeContext);
  const { isAuthenticated } = useContext(AuthContext);

  const { data: unreadData } = useQuery({
    queryKey: ["notifications-unread-count"],
    queryFn: notificationsService.getUnreadCount,
    refetchInterval: 30000,
    refetchIntervalInBackground: true,
    enabled: isAuthenticated,
  });

  const { data: notifications } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => notificationsService.getNotifications({ limit: 20 }),
    refetchInterval: isOpen ? 30000 : false,
    enabled: isOpen,
  });

  const markReadMutation = useMutation({
    mutationFn: notificationsService.markAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({ queryKey: ["notifications-all"] });
      queryClient.invalidateQueries({ queryKey: ["notifications-unread-count"] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: notificationsService.markAllRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({ queryKey: ["notifications-all"] });
      queryClient.invalidateQueries({ queryKey: ["notifications-unread-count"] });
    },
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const unreadCount = unreadData?.count || 0;

  useEffect(() => {
    document.title = formatTabTitle(unreadCount, document.title);
  }, [unreadCount]);

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markReadMutation.mutate(notification.id);
    }
    if (notification.action_url) {
      navigate(notification.action_url);
      // navigate() is a no-op when already on the target route, so the feed
      // (which refetches on this event) won't update. Nudge it explicitly.
      if (notification.action_url === location.pathname) {
        window.dispatchEvent(new CustomEvent("recognitionCreated"));
      }
      setIsOpen(false);
    }
  };

  return (
    <div className="relative mr-1" ref={panelRef}>
      <button
        className="relative p-2 hover:bg-white/20 rounded-full transition-colors duration-75 text-white"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t("bell.aria_label")}
      >
        <Bell size={20} className="translate-y-[1px]" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 border-2 border-white/30">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className="absolute right-0 mt-3 w-80 sm:w-96 rounded-2xl shadow-2xl overflow-hidden z-50 border border-gray-200 bg-white"
          >
            {/* Header */}
            <div
              className="px-4 py-3 flex items-center justify-between"
              style={{ background: `linear-gradient(to right, ${orgTheme.primaryColor}, ${orgTheme.secondaryColor})` }}
            >
              <h3 className="text-white font-semibold text-sm">{t("bell.title")}</h3>
              {unreadCount > 0 && (
                <button
                  onClick={() => markAllReadMutation.mutate()}
                  className="text-white/80 hover:text-white text-xs flex items-center gap-1 transition-colors"
                >
                  <CheckCheck size={14} />
                  {t("bell.mark_all_read")}
                </button>
              )}
            </div>

            {/* Notification List */}
            <div className="max-h-96 overflow-y-auto">
              {!notifications || notifications.length === 0 ? (
                <div className="py-12 text-center text-gray-400 text-sm">
                  <Bell size={32} className="mx-auto mb-2 opacity-30" />
                  {t("bell.empty")}
                </div>
              ) : (
                notifications.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => handleNotificationClick(n)}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-gray-100/50 transition-colors border-b border-gray-100 last:border-b-0 ${
                      !n.is_read ? "bg-blue-100/50" : ""
                    }`}
                  >
                    <div className="mt-0.5 flex-shrink-0">
                      {typeIcons[n.type] || <Bell size={16} className="text-gray-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-sm truncate ${!n.is_read ? "font-semibold text-gray-900" : "text-gray-700"}`}>
                          {n.title}
                        </p>
                        {!n.is_read && (
                          <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate mt-0.5">{n.message}</p>
                      <p className="text-[10px] text-gray-400 mt-1">{fmt.relative(n.created_at)}</p>
                    </div>
                  </button>
                ))
              )}
            </div>

            {/* Footer */}
            {notifications && notifications.length > 0 && (
              <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50">
                <button
                  onClick={() => {
                    navigate("/notifications");
                    setIsOpen(false);
                  }}
                  className="w-full text-center text-xs font-medium hover:underline"
                  style={{ color: orgTheme.primaryColor }}
                >
                  {t("bell.view_all")}
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationBell;
